import { resolveActionCards } from './resolveActionCards';
import { hasPremiumFeature } from '@lub0v/santa-utils';
import { createElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo, PremiumDrawUsage } from '@lub0v/santa-utils';
import { showDialog, showToast } from '@src/components';
import { santaClient } from '@src/state/client';
import { DrawPreview } from './DrawPreview';
import { insertPreviewParticipant, type DrawAssignment } from './draw';

export function useEditBoxDraw(info: BoxInfo, source: CardInfo[] | (() => Promise<CardInfo[]>), onChanged: () => Promise<void> | void) {
  const { t } = useTranslation();
  return async () => {
    const cards = await resolveActionCards(source);
    if (!cards) return;
    if (!info.isAdmin || !info.isActiveDraw || info.box.isArchived) return;
    const previousAssignments = cards.map(({ id, wardId }) => ({ id, wardId: wardId || 0 }));
    const missingIds = previousAssignments.filter(pair => !pair.wardId).map(pair => pair.id);
    const additionalDraw = !hasPremiumFeature(info.box, 'boxDraw') && missingIds.length > 1;
    const freeRepair = !hasPremiumFeature(info.box, 'boxDraw') && missingIds.length === 1;
    const premiumUsage = new Set<PremiumDrawUsage>();
    let additionalReady = false;
    let repair: DrawAssignment | undefined;
    let assignments: DrawAssignment[] = previousAssignments;
    showDialog({
      title: t('game.draw.completed.viewOrEdit'),
      renderBody: (busy, setSubmitDisabled, setSubmitSummary) => createElement(DrawPreview, { cards, onPremiumUsage: (usage) => { premiumUsage.add(usage); }, initial: previousAssignments, freeRepair, onAdditionalPrepare: () => { additionalReady = true; setSubmitDisabled(false); }, additionalCount: additionalDraw ? missingIds.length : 0, onRepairChange: (pair) => { repair = pair; setSubmitDisabled(false); }, circleDraw: Boolean(info.box.useCircleDraw), premium: Boolean(hasPremiumFeature(info.box, 'boxDraw')), existingDraw: true, disabled: busy, onIncompleteChange: additionalDraw ? () => setSubmitDisabled(!additionalReady) : freeRepair ? () => setSubmitDisabled(!repair) : setSubmitDisabled, onSaveSummaryChange: setSubmitSummary, onChange: (next) => { assignments = next; } }),
      cancel: hasPremiumFeature(info.box, 'boxDraw') || freeRepair || additionalDraw ? t('box.cancel') : undefined,
      submit: t(hasPremiumFeature(info.box, 'boxDraw') || freeRepair || additionalDraw ? 'game.draw.editExisting.save' : 'box.close'),
      mobileFullScreen: true,
      testId: 'game-edit-draw-dialog',
      onSubmit: !hasPremiumFeature(info.box, 'boxDraw') && !freeRepair && !additionalDraw ? undefined : async () => {
        if (additionalDraw) {
          if (!additionalReady) throw new Error('Additional draw not prepared');
          const result = await santaClient.draw({ key: info.box.key, cardIds: missingIds, assignments });
          if (!result.ok) { showToast(t('game.draw.failed')); throw new Error('Additional draw failed'); }
          await onChanged();
          return;
        }
        const insertion = repair ? insertPreviewParticipant(cards, previousAssignments, repair.id, repair.wardId) : null;
        const onlyNewcomerInsertion = insertion && assignments.every(pair => insertion.some(expected => expected.id === pair.id && expected.wardId === pair.wardId));
        if (freeRepair || onlyNewcomerInsertion) {
          if (!repair) throw new Error('No repair selected');
          const result = await santaClient.updateOtherCard({ key: info.box.key, id: repair.id, wardId: repair.wardId });
          if (!result.ok) { showToast(t('game.draw.failed')); throw new Error('Draw repair failed'); }
          await onChanged();
          return;
        }
        if (assignments.some(pair => !pair.wardId)) { showToast(t('game.draw.manual.incomplete')); throw new Error('Incomplete draw'); }
        const newcomerIds = new Set(missingIds);
        const newcomerOnly = missingIds.length > 1 && assignments.every(pair => newcomerIds.has(pair.id)
          ? newcomerIds.has(pair.wardId)
          : pair.wardId === previousAssignments.find(original => original.id === pair.id)?.wardId);
        const result = await santaClient.draw(newcomerOnly
          ? { key: info.box.key, cardIds: missingIds, assignments }
          : { key: info.box.key, assignments, previousAssignments, premiumUsage: [...premiumUsage], editExisting: true, circleDraw: Boolean(info.box.useCircleDraw) });
        if (!result.ok) {
          showToast(t(result.error?.message === 'DRAW_PREVIEW_CHANGED' ? 'game.draw.editExisting.stale' : 'game.draw.failed'));
          throw new Error('Draw edit failed');
        }
        await onChanged();
      },
    });
  };
}
